import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useZoomImages } from '../utils/imageQueue';

// Полноэкранный просмотр фото карточки: тап по фото открывает, свайп и стрелки
// листают (вверх/вниз — закрыть), крестик/фон/Escape закрывают. Рендер через
// портал в body — иначе position:fixed «прилипает» к карточке (у предков есть
// transform, который делает их containing block-ом для fixed).
// panel — «вложенное окно товара»: под фото информация карточки и действия.
// На мобильном panel-режим занимает весь экран, на десктопе (≥768px) —
// центрированная модалка (стили .quickview-* в styles.css).
// images — большие фото, previews — средние того же фото (уже скачаны каталогом): пока
// большое качается, на его месте стоит среднее, потом подменяется (utils/imageQueue.js —
// большие по одному, с текущего и дальше по порядку; каталог на это время на паузе).
function PhotoLightbox({ images, previews = null, startIndex = 0, alt = '', onClose, onIndexChange, panel = null }) {
  const [index, setIndex] = useState(startIndex);
  const touchRef = useRef(null);
  const total = images.length;
  const light = !!panel;
  const fullReady = useZoomImages(previews ? images : [], index);
  const shownSrc = previews && previews[index] && !fullReady[index] ? previews[index] : images[index];

  const goTo = useCallback((i) => {
    const next = (i + total) % total;
    setIndex(next);
    onIndexChange && onIndexChange(next);
  }, [total, onIndexChange]);

  const prev = useCallback(() => goTo(index - 1), [goTo, index]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);

  // Блокируем скролл страницы, пока открыт просмотр
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Клавиатура (десктоп): Escape / стрелки
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, prev, next]);

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };

  const suppressClickRef = useRef(false);
  // Момент открытия: на планшетах/Android синтезированный click после тапа по
  // карточке хит-тестится уже по появившемуся оверлею и мгновенно закрывал окно
  const mountedAtRef = useRef(Date.now());

  const cardRef = useRef(null);
  const photoRef = useRef(null);

  // Карточка/оверлей в самом верху? (мобайл скроллит карточку, десктоп — оверлей)
  const isAtTop = () => {
    const c = cardRef.current;
    if (!c) return true;
    const ov = c.parentElement;
    return c.scrollTop <= 0 && (!ov || ov.scrollTop <= 0);
  };

  // Карточка/оверлей домотаны до конца?
  const isAtBottom = () => {
    const c = cardRef.current;
    if (!c) return true;
    const ov = c.parentElement;
    const el = c.scrollHeight > c.clientHeight + 2
      ? c
      : (ov && ov.scrollHeight > ov.clientHeight + 2 ? ov : null);
    if (!el) return true; // скроллить нечего — обе границы «здесь»
    return el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
  };

  // Жест «выход в каталог» с ОБЕИХ границ: вниз из верхнего положения и вверх,
  // когда карточка домотана до конца. Слушаем ВСЮ карточку (в конце палец на
  // панели, не на фото) нативно: iOS перехватывает вертикальный жест как скролл
  // и touchend не доходит — на touchmove блокируем скролл только для жеста
  // закрытия, обычная прокрутка не затрагивается.
  useEffect(() => {
    if (!light) return;
    const card = cardRef.current;
    if (!card) return;
    let sx = null, sy = null, atTop0 = false, atBot0 = false, lock = null;
    const onStart = (e) => {
      const t = e.touches[0];
      sx = t.clientX; sy = t.clientY;
      atTop0 = isAtTop(); atBot0 = isAtBottom();
      lock = null;
    };
    const onMove = (e) => {
      if (sy == null) return;
      const t = e.touches[0];
      const dx = t.clientX - sx;
      const dy = t.clientY - sy;
      if (lock == null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        const vertical = Math.abs(dy) > Math.abs(dx);
        lock = (vertical && ((dy > 0 && atTop0) || (dy < 0 && atBot0))) ? 'close' : 'native';
      }
      if (lock === 'close') e.preventDefault();
    };
    const onEnd = (e) => {
      if (sy == null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - sx;
      const dy = t.clientY - sy;
      const closing = lock === 'close' && Math.abs(dy) > 60 && Math.abs(dy) > Math.abs(dx);
      sx = null; sy = null; lock = null;
      if (closing) {
        suppressClickRef.current = true;
        onClose();
      }
    };
    card.addEventListener('touchstart', onStart, { passive: true });
    card.addEventListener('touchmove', onMove, { passive: false });
    card.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      card.removeEventListener('touchstart', onStart);
      card.removeEventListener('touchmove', onMove);
      card.removeEventListener('touchend', onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [light, onClose]);

  const handleTouchEnd = (e) => {
    if (!touchRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    touchRef.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      // горизонтальный свайп — листаем и глушим синтезированный click
      suppressClickRef.current = true;
      if (dx < 0) next(); else prev();
    } else if (Math.abs(dy) > 60 && Math.abs(dy) > Math.abs(dx)) {
      // В panel-режиме вертикальные жесты обрабатывает нативный слушатель на
      // всей карточке (закрытие с обеих границ) — здесь только тёмный просмотр.
      if (!light) {
        suppressClickRef.current = true;
        onClose();
      }
    }
  };

  const handleOverlayClick = () => {
    if (Date.now() - mountedAtRef.current < 400) return; // призрачный click после открытия
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onClose();
  };

  // Верхние контролы — ниже статус-бара и плавающих кнопок Telegram (в
  // полноэкранном режиме «Закрыть»/«…» Телеграма висят сверху и закрывают
  // ПРИЛОЖЕНИЕ — наш крестик не должен прятаться под ними). Вне TG обе
  // переменные нулевые и отступ остаётся маленьким.
  const safeTop = 'var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px)';
  const controlsTop = (extra) => `calc(${safeTop} + ${extra}px)`;

  // В panel-режиме у .quickview-photo padding-top = safeTop + 52px (полоса под
  // счётчик и крестик), картинка центрируется в области ПОД ним. «50%» контейнера —
  // выше центра картинки на половину этого отступа, компенсируем.
  const navBtnStyle = (side) => ({
    position: 'absolute',
    top: light ? `calc(50% + (${safeTop} + 52px) / 2)` : '50%',
    transform: 'translateY(-50%)', [side]: 8,
    width: 44, height: 44, borderRadius: '50%', border: 'none',
    background: light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)',
    color: light ? '#1A1A1A' : '#fff',
    fontSize: 26, lineHeight: 1, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  const controls = (
    <>
      {total > 1 && (
        <div style={{
          position: 'absolute',
          top: controlsTop(12),
          left: '50%', transform: 'translateX(-50%)',
          color: light ? '#1A1A1A' : '#fff', fontSize: 14, fontWeight: 600,
          background: light ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.4)',
          borderRadius: 12, padding: '4px 12px',
        }}>
          {index + 1} / {total}
        </div>
      )}

      {/* Назад (слева, вместо крестика) — закрывает просмотр фото, не приложение */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Назад"
        style={{
          position: 'absolute',
          top: controlsTop(8),
          left: 10,
          width: 40, height: 40, borderRadius: '50%', border: 'none',
          background: light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.2)',
          color: light ? '#1A1A1A' : '#fff',
          fontSize: 22, lineHeight: 1, cursor: 'pointer',
          zIndex: 2,
        }}
      >←</button>

      {total > 1 && (
        <>
          <button style={navBtnStyle('left')} aria-label="Предыдущее фото"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            onTouchEnd={(e) => e.stopPropagation()}>‹</button>
          <button style={navBtnStyle('right')} aria-label="Следующее фото"
            onClick={(e) => { e.stopPropagation(); next(); }}
            onTouchEnd={(e) => e.stopPropagation()}>›</button>
        </>
      )}
    </>
  );

  // Тёмный просмотр на весь экран: фото по размеру окна, а не по своим пикселям — иначе
  // среднее (600 px) и подменяющее его большое (900 px) на широком экране разного размера
  const img = (
    <img
      src={shownSrc}
      alt={alt}
      draggable={false}
      onClick={(e) => e.stopPropagation()}
      style={{
        maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
        ...(light ? {} : { width: '100%', height: '100%' }),
        userSelect: 'none', WebkitUserSelect: 'none',
      }}
    />
  );

  if (!light) {
    // Тёмный полноэкранный просмотр (без панели)
    return createPortal(
      <div
        onClick={handleOverlayClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'fixed', inset: 0, zIndex: 10500,
          background: 'rgba(0,0,0,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {img}
        {controls}
      </div>,
      document.body
    );
  }

  // Quick-view товара: мобайл — во весь экран, десктоп/планшет — модалка.
  // Свайп-жесты (листание/закрытие) висят ТОЛЬКО на фото-зоне — иначе попытка
  // прокрутить длинную панель (состав) считывалась как «смахнуть» и закрывала окно.
  return createPortal(
    <div className="quickview-overlay" onClick={handleOverlayClick}>
      <div className="quickview-card" ref={cardRef} onClick={(e) => e.stopPropagation()}>
        {/* Контролы внутри фото-зоны: при длинной панели (состав) стрелки
            остаются по центру ФОТО, а не всего окна */}
        <div
          className="quickview-photo"
          ref={photoRef}
          style={{ position: 'relative' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {img}
          {controls}
        </div>
        <div className="quickview-panel">{panel}</div>
      </div>
    </div>,
    document.body
  );
}

export default PhotoLightbox;
